import { FC, useState, useEffect } from "react";
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Gamepad2, Trophy, Settings2, Menu, X, MessageSquare } from "lucide-react";
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { StudentProgress } from '@/components/parent-dashboard/types';
import { ActivityTab } from '@/components/parent-dashboard/ActivityTab';
import { ProgressTab } from '@/components/parent-dashboard/ProgressTab';
import { AchievementsTab } from '@/components/parent-dashboard/AchievementsTab';
import { SettingsTab } from '@/components/parent-dashboard/SettingsTab';
import { CommunityTab } from '@/components/parent-dashboard/CommunityTab';

const getGameName = (gameId: string): string => {
  const gameNames: { [key: string]: string } = {
    'game1': 'Ahorro Aventura',
    'game2': 'Presupuesto Master',
    'game3': 'Inversión Quest',
  };
  return gameNames[gameId] || gameId;
};

export const ParentDashboard: FC = () => {
  const [activeTab, setActiveTab] = useState("activity");
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { user } = useAuth();
  const { userProfile } = useProfile();

  const { data: studentProgress, isLoading } = useQuery({
    queryKey: ["studentProgress", user?.id],
    queryFn: async () => {
      try {
        if (!user?.id) return null;

        // Fetch all necessary data
        const [lessonProgressResult, gameProgressResult, achievementsResult] = await Promise.all([
          supabase
            .from('student_progress')
            .select('lesson_id, completed_at, time_spent')
            .eq('user_id', user.id),
          supabase
            .from('game_progress')
            .select('game_id, played_at, time_played')
            .eq('user_id', user.id),
          supabase
            .from('achievements')
            .select('achievement_id, unlocked_at')
            .eq('user_id', user.id)
        ]);

        // Process the data
        const progress: StudentProgress = {
          total_lessons: 20,
          completed_lessons: lessonProgressResult.data?.length || 0,
          total_games: 6,
          games_played: new Set(gameProgressResult.data?.map(g => g.game_id) || []).size,
          achievements: achievementsResult.data?.length || 0,
          last_activities: [
            ...(lessonProgressResult.data?.map(l => ({
              date: l.completed_at,
              type: 'lesson' as const,
              name: `Lección ${l.lesson_id}`
            })) || []),
            ...(gameProgressResult.data?.map(g => ({
              date: g.played_at,
              type: 'game' as const,
              name: getGameName(g.game_id)
            })) || [])
          ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
          weekly_activity: Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const dayActivities = [
              ...(lessonProgressResult.data || []),
              ...(gameProgressResult.data || [])
            ].filter(activity => {
              const activityDate = new Date(activity.completed_at || activity.played_at);
              return activityDate.toDateString() === date.toDateString();
            });

            const totalMinutes = dayActivities.reduce((acc, curr) => 
              acc + (curr.time_spent || curr.time_played || 0), 0);

            return {
              date: date.toLocaleDateString('es-ES', { weekday: 'short' }),
              minutes: Math.round(totalMinutes / 60)
            };
          }).reverse()
        };

        return progress;
      } catch (error) {
        console.error('Error in queryFn:', error);
        return null;
      }
    },
    enabled: !!user?.id,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setIsSheetOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#F4F7FE] pb-4 md:pb-8">
      <div className="container mx-auto px-3 md:px-4 pt-4 md:pt-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E6EDF9] p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4">
            <div>
              <h1 className="text-xl md:text-2xl lg:text-4xl font-bold mb-2 text-[#2B3674]">Panel de Control</h1>
              <p className="text-[#707EAE] text-sm md:text-base">
                ¡Bienvenido/a{userProfile?.tutor_name ? `, ${userProfile.tutor_name}` : ''}! 
                {userProfile?.student_name && (
                  <span> Aquí podrás monitorear el progreso de <span className="font-semibold text-[#4318FF]">{userProfile.student_name}</span> en su viaje de educación financiera. 
                  Mantente al día con sus actividades, logros y avances.</span>
                )}
              </p>
            </div>
            <Button 
              asChild 
              variant="outline" 
              size="sm"
              className="w-full md:w-auto border-[#4318FF] text-[#4318FF] hover:bg-[#4318FF] hover:text-white transition-all"
            >
              <Link to="/dashboard" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Volver al Dashboard
              </Link>
            </Button>
          </div>
        </div>

        {/* Alerta para estudiantes */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E6EDF9] p-4 md:p-6 mb-4 md:mb-6">
          <div className="p-3 md:p-4 bg-[#FFF4DE] border border-[#FFB547] rounded-lg">
            <p className="text-[#B37B24] font-medium flex items-center gap-2 text-sm md:text-base">
              <span>👀</span>
              ¡Ups! Si eres estudiante, ¡esta no es tu área! Mejor vuelve a jugar y aprender en tu dashboard.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E6EDF9] p-3 md:p-4 lg:p-6">
          {/* Desktop Tabs */}
          <div className="hidden md:block">
            <Tabs defaultValue="activity" value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="w-full justify-start border-b mb-4 md:mb-6">
                <TabsTrigger value="activity" className="text-sm md:text-base">
                  <BookOpen className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="ml-2 hidden md:inline">Actividad</span>
                </TabsTrigger>
                <TabsTrigger value="progress" className="text-sm md:text-base">
                  <Trophy className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="ml-2 hidden md:inline">Progreso</span>
                </TabsTrigger>
                <TabsTrigger value="community" className="text-sm md:text-base">
                  <MessageSquare className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="ml-2 hidden md:inline">Comunidad</span>
                </TabsTrigger>
                <TabsTrigger value="achievements" className="text-sm md:text-base">
                  <Gamepad2 className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="ml-2 hidden md:inline">Logros</span>
                </TabsTrigger>
                <TabsTrigger value="settings" className="text-sm md:text-base">
                  <Settings2 className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="ml-2 hidden md:inline">Configuración</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="activity">
                <ActivityTab studentProgress={studentProgress} />
              </TabsContent>
              <TabsContent value="progress">
                <ProgressTab studentProgress={studentProgress} />
              </TabsContent>
              <TabsContent value="community">
                <CommunityTab />
              </TabsContent>
              <TabsContent value="achievements">
                <AchievementsTab studentProgress={studentProgress} />
              </TabsContent>
              <TabsContent value="settings">
                <SettingsTab />
              </TabsContent>
            </Tabs>
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full flex justify-between items-center mb-4">
                  <span className="flex items-center gap-2 text-sm">
                    {activeTab === "activity" && <><BookOpen className="h-4 w-4" /> Actividad</>}
                    {activeTab === "progress" && <><Trophy className="h-4 w-4" /> Progreso</>}
                    {activeTab === "community" && <><MessageSquare className="h-4 w-4" /> Comunidad</>}
                    {activeTab === "achievements" && <><Gamepad2 className="h-4 w-4" /> Logros</>}
                    {activeTab === "settings" && <><Settings2 className="h-4 w-4" /> Configuración</>}
                  </span>
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle>Menú</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-6">
                  <Button
                    variant={activeTab === "activity" ? "default" : "ghost"}
                    onClick={() => handleTabChange("activity")}
                    className="justify-start"
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Actividad
                  </Button>
                  <Button
                    variant={activeTab === "progress" ? "default" : "ghost"}
                    onClick={() => handleTabChange("progress")}
                    className="justify-start"
                  >
                    <Gamepad2 className="h-4 w-4 mr-2" />
                    Progreso
                  </Button>
                  <Button
                    variant={activeTab === "community" ? "default" : "ghost"}
                    onClick={() => handleTabChange("community")}
                    className="justify-start"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Comunidad
                  </Button>
                  <Button
                    variant={activeTab === "achievements" ? "default" : "ghost"}
                    onClick={() => handleTabChange("achievements")}
                    className="justify-start"
                  >
                    <Trophy className="h-4 w-4 mr-2" />
                    Logros
                  </Button>
                  <Button
                    variant={activeTab === "settings" ? "default" : "ghost"}
                    onClick={() => handleTabChange("settings")}
                    className="justify-start"
                  >
                    <Settings2 className="h-4 w-4 mr-2" />
                    Configuración
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <div>
              {activeTab === "activity" && <ActivityTab studentProgress={studentProgress} />}
              {activeTab === "progress" && <ProgressTab studentProgress={studentProgress} />}
              {activeTab === "community" && <CommunityTab />}
              {activeTab === "achievements" && <AchievementsTab studentProgress={studentProgress} />}
              {activeTab === "settings" && <SettingsTab />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;
